(ns frontend.view.nev.surveys.survey-profile
  (:require
   [common.entity :as entity]
   [common.schema.entities.rotation-period-override.options :as override-options]
   [common.intelligence.common :as ic]
   [common.languages :as languages]
   [common.localization :as loc]
   [frontend.db :as app-db]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity :as frontend-entity]
   [frontend.view.modals.entity-editor.view :as entity-editor-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.nev.events :as nev-events]
   [frontend.view.nev.surveys.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.view.nev.view :as nev-2024]))

(defn toolbar-buttons
  [{:keys [entity]}]
  (let [entity-id (:db/id entity)
        new-entity-viewer-preview-2024? @(r/track session/new-company-viewer-preview-2024?)]
    (concat
     (when new-entity-viewer-preview-2024?
       (zap-toolbar/new-entity-viewer-2024-toggle-and-divider))

     (when-not (:common/disabled? entity)
       [(zap-toolbar/delete-button entity [:surveys-grid (:survey-profile/type entity)])])

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(defn survey-processes
  [{:keys [entity]}]
  (let [processes (->> entity
                       :survey-profile/survey-processes
                       (keep (partial get @session/entities)))]
    [:div
     (when-not (session/is-read-only-user?)
       [:div.row.pl-3.pt-2
        (if-not (number? (:survey-profile/their-contact entity))
          (fu/tooltip (loc/tr :please-select-their-survey-contact)
                      [:div.disabled-button
                       (loc/tr :add- [(loc/tr :entity.type/survey-process)])])
          [button/sm-primary-solid
           {:disabled? (not (number? (:survey-profile/their-contact entity)))
            :on-click  (partial events/create-process! {:entity entity})}
           (loc/tr :add- [(loc/tr :entity.type/survey-process)])])])
     (when (seq processes)
       [:div
        {:class-name "py-3"}
        [:div.table-responsive
         [:table.zap-table.zap-table--striped
          {:style {:width "100%"}}
          [:thead.zap-table__head
           [:tr.zap-table__row
            [:th.zap-table__headline (loc/tr :survey-process/status)]
            [:th.zap-table__headline (loc/tr :survey-process/template)]
            [:th.zap-table__headline (loc/tr :survey-process/language)]
            [:th.zap-table__headline (loc/tr :common/created-on)]
            [:th.zap-table__headline (loc/tr :common/updated-on)]]]
          [:tbody.zap-table__body
           (->>
            processes
            (sort-by :common/updated-on)
            (reverse)
            (map
             (fn [survey-process]
               [:tr
                {:key        (str "survey-process-" (:db/id survey-process)
                                  "-" (pr-str (:survey-process/status survey-process)))
                 :class-name (fu/cs "zap-table__row" (pr-str (:survey-process/status survey-process)))
                 :on-click   (fn [_]
                               (when-not (session/is-read-only-user?)
                                 (routing/open-entity (:db/id survey-process) :profile-id (:db/id entity))))}
                [:td.zap-table__cell (entity/display-string (:survey-process/status survey-process))]
                [:td.zap-table__cell
                 (or (-> survey-process
                         :survey-process/template
                         (session/get-entity)
                         :survey-template/label)
                     "")]
                [:td.zap-table__cell (languages/get-label (:survey-process/language survey-process))]
                [:td.zap-table__cell (fu/format-date-ts (:common/created-on survey-process))]
                [:td.zap-table__cell (fu/format-date-ts (:common/updated-on survey-process))]]))
            (doall))]]]])]))

(defn override-type-label
  [override]
  (->> override-options/override-type-options
       (filter (fn [option]
                 (= (:key option) (:rotation-period-override/override-type override))))
       (first)
       (:label)))

(defn open-override-editor!
  "The profile is passed as `containing-entity-id` rather than left to the
   editor to work out. The override's rotation period and template pickers
   are scoped to the survey type behind the profile, and an override on its
   own cannot name that profile - the link lives on the profile as a
   card-many ref, and a client session has no reverse refs to walk back
   with."
  [{:keys [override-id survey-profile-id]}]
  (modals/open-modal! entity-editor-modal/modal-key
                      {:entity-id            override-id
                       :containing-entity-id survey-profile-id}))

(defn rotation-period-overrides
  "What this profile does differently in a given rotation period: skip it, or
   use another template for it.

   Both have been honoured by mass send since they were modelled - the
   preview reports that an override skips the profile for the selected cycle
   - but nothing in the application could create one, so until now they were
   reachable only through the API."
  [{:keys [entity]}]
  (let [overrides (->> entity
                       :survey-profile/rotation-period-overrides
                       (keep (partial get @session/entities))
                       ;; An id the session has not delivered yet renders as a
                       ;; row of nils whose edit button opens on a nil :db/id.
                       ;; Same guard as settings2.surveys.survey-type-tab.
                       (filter :db/id)
                       (sort-by :common/created-on))]
    [:div
     (when-not (session/is-read-only-user?)
       [:div.row.pl-3.pt-2
        [button/sm-primary-solid
         {:on-click (fn [_]
                      (frontend-entity/open-creator!
                       :entity.type/rotation-period-override
                       {:prevent-open-entity-after-create? true
                        :on-ok!                            (fn [{:keys [close-cb!]}] (close-cb!))
                        :start-value                       {:common/containing-entity (:db/id entity)}}))}
         (loc/tr :add- [(loc/tr :entity.type/rotation-period-override)])]])
     (when (seq overrides)
       [:div
        {:class-name "py-3"}
        [:div.table-responsive
         [:table.zap-table.zap-table--striped
          {:style {:width "100%"}}
          [:thead.zap-table__head
           [:tr.zap-table__row
            [:th.zap-table__headline (loc/tr :rotation-period-override/rotation-period)]
            [:th.zap-table__headline (loc/tr :rotation-period-override/override-type)]
            [:th.zap-table__headline (loc/tr :rotation-period-override/template)]]]
          [:tbody.zap-table__body
           (->> overrides
                (map (fn [override]
                       [:tr
                        {:key        (str "rotation-period-override-" (:db/id override))
                         :class-name "zap-table__row"
                         :on-click   (fn [_]
                                       (when-not (session/is-read-only-user?)
                                         (open-override-editor!
                                          {:override-id       (:db/id override)
                                           :survey-profile-id (:db/id entity)})))}
                        [:td.zap-table__cell
                         (or (some-> (:rotation-period-override/rotation-period override)
                                     (session/get-entity)
                                     :rotation-period/rotation-period-label)
                             "")]
                        [:td.zap-table__cell (or (override-type-label override) "")]
                        [:td.zap-table__cell
                         (or (some-> (:rotation-period-override/template override)
                                     (session/get-entity)
                                     :survey-template/label)
                             "")]]))
                (doall))]]]])]))

(defn survey-profile-view
  [{:keys [survey-profile-id]}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :survey-profile])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [survey-profile-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :survey-profile-id
               (partial nev-events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom survey-profile-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (nev-events/subscribe-to-changes! {:entity      {:db/id survey-profile-id}
                                           :on-updates! nev-events/updates-to-session-state}))
      :component-will-unmount
      (fn [_]
        (nev-events/unsubscribe-changes! {:entity {:db/id survey-profile-id}})
        (nev-events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [survey-profile-id] :as view-args}]
        (let [survey-profile (get @session/entities survey-profile-id)
              survey-type-id (:survey-profile/type survey-profile)
              enriched-entity (ic/enrich-entity survey-profile)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? survey-profile)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not survey-profile)
                    (:common/disabled? survey-profile))
              [:div " "]
              (let [;; Hardcoded rather than driven off the schema, so a new
                    ;; field on survey-profile is not editable until it is
                    ;; named here. The re-sharing default and its domain list
                    ;; are deliberately absent: they are :in-use-disabled
                    ;; until the re-sharing endpoint exists, and a disabled
                    ;; control promises a setting that nothing enforces yet.
                    header-rows [[:survey-profile/our-contact :survey-profile/their-contact]
                                 [:survey-profile/primary-manager :survey-profile/other-managers]
                                 [:survey-profile/recipients :survey-profile/default-auth-type]
                                 [:survey-profile/cc-type :survey-profile/other-cc-recipients]]
                    entity-template (entity-editor/resolve-template
                                     {:entity                @entity-atom
                                      :tenant-schema         @session/tenant-schema
                                      :account               @session/account
                                      :role                  @session/role
                                      :pipeline-id           nil
                                      :remove-fields         []
                                      :override-editor-types nil})
                    entity-template-lookup (->> entity-template
                                                (map (fn [field-schema]
                                                       [(:attribute field-schema)
                                                        field-schema]))
                                                (into {}))
                    view-id :survey-profile]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/view state-atom
                    (partial zap-toolbar/toolbar-back [:surveys-grid :id survey-type-id] (loc/tr :back-to/surveys-grid))
                    nil
                    (toolbar-buttons (assoc view-args
                                            :entity
                                            survey-profile))])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    [nev-components/header-module
                     {:component-state        temp-atom
                      :view-state             view-state-atom
                      :entity                 survey-profile
                      :enriched-entity        enriched-entity
                      :entity-atom            entity-atom
                      :entity-template-lookup entity-template-lookup
                      :field-rows             header-rows}]
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class "col-span-10 lg:col-span-5"}
                      [survey-processes
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          survey-profile
                        :enriched-entity enriched-entity
                        :entity-atom     entity-atom}]
                      [rotation-period-overrides
                       {:entity survey-profile}]]
                     [:div
                      {:class "col-span-10 lg:col-span-5"}
                      [modules/view
                       {:view-id             view-id
                        :ordering            (get-in @session/view-config [view-id :modules :ordering])
                        :save-modules-order! (partial nev-events/save-modules-order!
                                                      (:entity/type survey-profile))
                        :modules             [(nev-components/attachments-module view-id
                                                                                 :survey-profile-attachments
                                                                                 survey-profile)]}]
                      [event-pane/view
                       {:state             events-pane-atom
                        :primary-entity-id survey-profile-id
                        :entity-ids        [survey-profile-id]
                        :module-args       {:state-atom state-atom
                                            :view-id    view-id}}]]]]]]])))))})))

(defn new-survey-profile-viewer-2025
  [{:keys [survey-profile-id]}]
  (let [state-path [:nev-2024 :survey-profile survey-profile-id]]
    [:div
     [nev-2024/view-with-data-fetching
      {:state-path  state-path
       :entity-id   survey-profile-id
       :entity-type :entity.type/survey-profile}]]))

(defn survey-profile-view-wrapper
  [{:keys [survey-profile-id]}]
  (let [classic-entity-viewer-enabled? @(r/track session/classic-entity-viewer-enabled?)]
    (if classic-entity-viewer-enabled?
      [survey-profile-view {:survey-profile-id survey-profile-id}]
      [new-survey-profile-viewer-2025 {:survey-profile-id survey-profile-id}])))

(defmethod routing/render-view :entity.type/survey-profile
  [{:keys [route-params]}]
  (let [survey-profile-id (-> route-params
                              :id
                              js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page survey-profile"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "survey-profile-viewer-id-" survey-profile-id)}
     [survey-profile-view-wrapper {:survey-profile-id survey-profile-id}]]))
